// Firmware versions and release notes, for the update screen.
//
// The device reports one of:
//   "v0.6.41"                 local build
//   "v0.6.40-dev+3919ec0"     dev / PR build
//   "v1.1.0 (v0.6.29)"        production: product version (internal version)
// The internal version (SIT_FW_VERSION_BASE) identifies the code, so that is
// what we compare.
//
// OTA zips built by wearable-firmware's Tools/package_ota.sh carry a
// release.json ({ version, base_version, channel, commit, built_at, notes }),
// where `notes` is GitHub's generated release-notes markdown.

const SEMVER = /v?(\d+\.\d+\.\d+)/;

/** Internal version of a device/package version string, e.g. "v0.6.29". */
export const baseVersionOf = (version) => {
  if (!version) return null;
  const s = String(version);
  const inParens = /\(\s*v?(\d+\.\d+\.\d+)[^)]*\)/.exec(s);
  const m = inParens || SEMVER.exec(s);
  return m ? `v${m[1]}` : null;
};

/** -1 / 0 / 1 comparing two "vX.Y.Z" strings; null if either is unparseable. */
export const compareVersions = (a, b) => {
  const pa = SEMVER.exec(a || "");
  const pb = SEMVER.exec(b || "");
  if (!pa || !pb) return null;
  const x = pa[1].split(".").map(Number);
  const y = pb[1].split(".").map(Number);
  for (let i = 0; i < 3; i++) {
    if (x[i] !== y[i]) return x[i] > y[i] ? 1 : -1;
  }
  return 0;
};

/** release.json from a package → normalized object, or null if absent/bad. */
export const normalizeRelease = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const str = (v) => (typeof v === "string" && v.trim() ? v.trim() : null);
  const version = str(raw.version);
  return {
    version,
    baseVersion: str(raw.base_version) || baseVersionOf(version),
    channel: str(raw.channel),
    commit: str(raw.commit),
    dirty: raw.dirty === true,
    builtAt: str(raw.built_at),
    notes: str(raw.notes),
  };
};

// "* Title by @user in https://github.com/o/r/pull/42" → { text, pr: 42 }
const GITHUB_ITEM =
  /^(.*?)\s+by\s+@[\w-]+(?:\[bot\])?\s+in\s+(https?:\/\/\S+)\s*$/;

/**
 * GitHub release-notes markdown → [{ title, items: [{ text, pr }] }].
 * Keeps headings and bullet items; drops the "Full Changelog" footer and the
 * "New Contributors" section, which mean nothing on a device screen.
 */
export const parseReleaseNotes = (markdown) => {
  if (!markdown) return [];
  const sections = [];
  let current = null;
  let skipping = false;
  const section = (title) => {
    current = { title, items: [] };
    sections.push(current);
  };
  for (const raw of String(markdown).split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const heading = /^#{1,6}\s+(.*)$/.exec(line);
    if (heading) {
      skipping = /new contributors/i.test(heading[1]);
      if (!skipping) section(heading[1].trim());
      continue;
    }
    if (skipping || /^\*\*full changelog\*\*/i.test(line)) continue;
    const bullet = /^[*-]\s+(.*)$/.exec(line);
    let text = bullet ? bullet[1] : line;
    let pr = null;
    const gh = GITHUB_ITEM.exec(text);
    if (gh) {
      text = gh[1];
      const num = /\/pull\/(\d+)/.exec(gh[2]);
      if (num) pr = Number(num[1]);
    }
    text = text.replace(/\*\*(.+?)\*\*/g, "$1").replace(/`([^`]+)`/g, "$1");
    if (!current) section(null);
    current.items.push({ text, pr });
  }
  return sections.filter((s) => s.items.length);
};

// Conventional-commit prefixes ("feat(fw): ", "ci!: ") and ticket tags
// ("[SPO-1137] "). Other prefixes ("sync: ") say what changed, so they stay.
const CHANGE_TYPE =
  /^(feat|fix|perf|refactor|revert|ci|docs|chore|test|build|style)(?:\([^)]*\))?!?:\s*/i;
const TICKET = /^\[[A-Z]+-\d+\]\s*/;
// Kinds of PR that don't change what's on the device.
const INTERNAL_TYPES = ["ci", "docs", "chore", "test", "build", "style"];
// A trailing "(v0.6.30–v0.6.34)" or "(SPO-1352, v0.6.37)": internal versions.
const TRAILING_VERSIONS = /\s*\([^()]*\bv\d+\.\d+\.\d+[^()]*\)\s*$/;

/**
 * Release notes as an operator reads them: the hand-written "Highlights"
 * (the release workflow's `notes` input) apart from the PR list, with PR
 * titles cleaned of commit prefixes, ticket tags and internal version ranges,
 * and PRs that don't touch the device (CI, docs, chores) left out.
 * → { highlights: [{ text, pr }], changes: [{ text, pr }] }
 */
export const operatorNotes = (markdown) => {
  const highlights = [];
  const changes = [];
  for (const section of parseReleaseNotes(markdown)) {
    const isHighlights = /^highlights$/i.test(section.title || "");
    for (const item of section.items) {
      let text = item.text.replace(TICKET, "");
      const type = CHANGE_TYPE.exec(text);
      if (type) {
        if (!isHighlights && INTERNAL_TYPES.includes(type[1].toLowerCase())) {
          continue;
        }
        text = text.slice(type[0].length);
      }
      text = text.replace(TICKET, "").replace(TRAILING_VERSIONS, "").trim();
      if (!text) continue;
      text = text[0].toUpperCase() + text.slice(1);
      (isHighlights ? highlights : changes).push({ text, pr: item.pr });
    }
  }
  return { highlights, changes };
};
